import { AfterViewChecked, Component, ElementRef, inject } from '@angular/core';
import { AbstractComponent } from '@sol/components/components/abstract.component';
import { TooltipService, TranslatePipe } from '@sol/components';
import { SolTooltipsState } from './lab-tooltips.state';
import { cssTimeToMs } from '../../tools/css-time';

// Singleton holds an exiting overlay in the DOM for the same duration as the
// CSS fade-out so it can animate before removal.
const EXIT_DURATION_MS = cssTimeToMs('--sol-motion-base', 180);

@Component({
  selector: 'lab-tooltips',
  standalone: true,
  imports: [TranslatePipe],
  templateUrl: './lab-tooltips.component.html',
})
export class LabTooltipsComponent extends AbstractComponent<SolTooltipsState> implements AfterViewChecked {
  // Dependencies
  private readonly tooltipService = inject(TooltipService);
  private readonly elementRef = inject(ElementRef);

  // Computed
  protected readonly visible$ = this.compute(state => state.visible);
  protected readonly overlays$ = this.compute(state => state.overlays);

  // Internal
  private clampedIds = new Set<string>();
  private scheduledExits = new Set<string>();

  constructor() {
    super(SolTooltipsState.of());

    this.tooltipService.watch(state => {
      this.update(s => s.withTooltips(state.tooltips));
      this.scheduleExits();
    });
  }

  private scheduleExits(): void {
    for (const overlay of this.state.overlays) {
      if (!overlay.exiting || this.scheduledExits.has(overlay.id)) {
        continue;
      }
      this.scheduledExits.add(overlay.id);
      setTimeout(() => {
        // If the user re-hovered during the exit window, the overlay was
        // resurrected (exiting: false). Don't yank it from under them.
        this.update(s => {
          const current = s.overlays.find(o => o.id === overlay.id);
          return current?.exiting ? s.withOverlayRemoved(overlay.id) : s;
        });
        this.scheduledExits.delete(overlay.id);
      }, EXIT_DURATION_MS);
    }
  }

  public ngAfterViewChecked(): void {
    const overlays = this.state.overlays;
    for (const overlay of overlays) {
      // Only clamp once per show cycle
      if (this.clampedIds.has(overlay.id)) {
        continue;
      }
      const el = this.elementRef.nativeElement.querySelector(`[data-tooltip-id="${overlay.id}"]`);
      if (!el) {
        continue;
      }
      const rect = el.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) {
        continue;
      }
      this.clampedIds.add(overlay.id);
      this.update(state => state.withViewportClamp(overlay.id, rect));
    }

    // Clean up clamped IDs for removed tooltips
    const activeIds = new Set(overlays.map(o => o.id));
    for (const id of this.clampedIds) {
      if (!activeIds.has(id)) {
        this.clampedIds.delete(id);
      }
    }
  }
}
